import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { serverUpdateScreensharing } from "api/api-server-actions";
import { FOLLOWING_QUERY_PARAM } from "components/constants";
import { PillBanner, PillBannerColorType } from "components/footer/PillBanner";
import { AutoFollowButtons } from "components/materials/presentation/AutoFollowButtons";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { PillButton } from "mds/components/PillButton";
import { useIsLgOrLarger } from "mds/hooks/use-responsive";
import { ScreenshareOutlineIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectUserByClassUserId } from "store/selectors";
import { selectCurrentBreakoutGroupId } from "store/selectors/breakouts";
import { selectActiveClassSessionId } from "store/selectors/class-session";
import { selectCurrentClassUser } from "store/selectors/class-user";
import {
  selectScreensharingClassUserId,
  selectViewingScreenshare,
} from "store/selectors/virtual-classes";
import { viewStateActions } from "store/slices/view";
import { useAv } from "utils/av";
import { usePresentation } from "utils/presentation";
import { useQueryParamsManager } from "utils/routing";

const StopSharingButton: FC = () => {
  const activeClassSessionId = useAppSelector(selectActiveClassSessionId);
  const currentBreakoutGroupId = useAppSelector(selectCurrentBreakoutGroupId);
  const showShortText = !useIsLgOrLarger();

  const handleClick = useCallback(async () => {
    if (!activeClassSessionId) return;

    await serverUpdateScreensharing(activeClassSessionId, null, currentBreakoutGroupId);
  }, [activeClassSessionId, currentBreakoutGroupId]);

  return (
    <PillButton eventName="Screensharing - Stop" kind="banner-green" size="s" onClick={handleClick}>
      {showShortText ? t("common.stop") : t("av.screenshare.stop_sharing")}
    </PillButton>
  );
};

const EndScreenshareButton: FC = () => {
  const activeClassSessionId = useAppSelector(selectActiveClassSessionId);
  const currentBreakoutGroupId = useAppSelector(selectCurrentBreakoutGroupId);

  const handleClick = useCallback(async () => {
    if (!activeClassSessionId) return;

    await serverUpdateScreensharing(activeClassSessionId, null, currentBreakoutGroupId);
  }, [activeClassSessionId, currentBreakoutGroupId]);

  return (
    <PillButton
      eventName="Screensharing - End for another user"
      kind="banner-light-gray"
      size="s"
      onClick={handleClick}
    >
      {t("av.screenshare.end_screenshare")}
    </PillButton>
  );
};

const StopFollowingScreenshareButton: FC = () => {
  const dispatch = useAppDispatch();
  const { deleteQueryParams } = useQueryParamsManager();

  const handleClick = useCallback(() => {
    dispatch(viewStateActions.setViewingScreenshare(false));
    deleteQueryParams([FOLLOWING_QUERY_PARAM]);
  }, [dispatch, deleteQueryParams]);

  return (
    <PillButton
      eventName="Screensharing - Stop following"
      kind="banner-light-gray"
      size="s"
      onClick={handleClick}
    >
      {t("common.stop")}
    </PillButton>
  );
};

const ViewScreenshareButton: FC = () => {
  const dispatch = useAppDispatch();

  const handleClick = useCallback(() => {
    dispatch(viewStateActions.setViewingScreenshare(true));
  }, [dispatch]);

  return (
    <PillButton
      eventName="Screensharing - Start following"
      kind="banner-orange"
      size="s"
      onClick={handleClick}
    >
      {t("common.return")}
    </PillButton>
  );
};

const FollowScreenshareButton: FC = () => (
  <AutoFollowButtons allowAutoFollow={false} screenSharing />
);

const AutoFollowScreenshareButtons: FC = () => <AutoFollowButtons allowAutoFollow screenSharing />;

const getPillBannerState = (
  isMyScreenshare: boolean,
  isFollowing: boolean,
  isViewingScreenshare: boolean,
  isInstructor: boolean,
  userName: string,
  autoFollow: boolean,
  showShortText: boolean,
) => {
  if (isMyScreenshare) {
    return {
      bannerText: t("av.screenshare.banner.screensharing"),
      Button: StopSharingButton,
      bannerColor: "green",
    };
  }

  if (isViewingScreenshare) {
    if (isInstructor) {
      return {
        bannerText: showShortText
          ? t("av.screenshare.banner.following_screenshare_short")
          : t("av.screenshare.banner.following_screenshare", { user: userName }),
        Button: EndScreenshareButton,
        bannerColor: "dark-gray",
      };
    }
    return {
      bannerText: showShortText
        ? t("av.screenshare.banner.following_screenshare_short")
        : t("av.screenshare.banner.following_screenshare", { user: userName }),
      Button: StopFollowingScreenshareButton,
      bannerColor: "dark-gray",
    };
  }

  if (isFollowing && !isViewingScreenshare) {
    return {
      bannerText: t("av.screenshare.banner.not_following_screenshare", { user: userName }),
      Button: ViewScreenshareButton,
      bannerColor: "orange",
    };
  }

  return {
    bannerText: showShortText
      ? t("av.screenshare.banner.screenshare_in_progress_short")
      : t("av.screenshare.banner.screenshare_in_progress", { user: userName }),
    Button: autoFollow ? AutoFollowScreenshareButtons : FollowScreenshareButton,
    bannerColor: "green",
  };
};

interface ScreenshareOptionsProps {
  autoFollow?: boolean;
}

export const ScreenshareOptions: FC<ScreenshareOptionsProps> = ({ autoFollow = false }) => {
  const currentClassUser = useAppSelector(selectCurrentClassUser);
  const screensharingClassUserId = useAppSelector(selectScreensharingClassUserId);
  const screensharingUser = useAppSelector((state) =>
    selectUserByClassUserId(state, screensharingClassUserId),
  );
  const { canAuthorSection } = useCapabilities();
  const isViewingScreenshare = useAppSelector(selectViewingScreenshare);
  const { isInVirtualClass } = useAv();
  const userName = screensharingUser?.first_name || "";
  const isMyScreenshare = !!(
    screensharingClassUserId && currentClassUser?.id === screensharingClassUserId
  );
  const { isFollowing } = usePresentation();
  const isLargeScreen = useIsLgOrLarger();

  // Track if user has ever followed this screenshare session - if so, don't auto-follow again
  const [hasFollowedThisSession, setHasFollowedThisSession] = useState(false);
  useEffect(() => {
    if (isViewingScreenshare) {
      setHasFollowedThisSession(true);
    }
  }, [isViewingScreenshare]);

  // Reset when screenshare ends
  useEffect(() => {
    if (!screensharingClassUserId) {
      setHasFollowedThisSession(false);
    }
  }, [screensharingClassUserId]);

  // Only show auto-follow if user hasn't already followed during this screenshare session
  const shouldAutoFollow = autoFollow && !hasFollowedThisSession;

  const {
    bannerText,
    Button: BannerButton,
    bannerColor,
  } = useMemo(
    () =>
      getPillBannerState(
        isMyScreenshare,
        isFollowing,
        isViewingScreenshare,
        canAuthorSection,
        userName,
        shouldAutoFollow,
        !isLargeScreen,
      ),
    [
      isMyScreenshare,
      isFollowing,
      isViewingScreenshare,
      canAuthorSection,
      userName,
      shouldAutoFollow,
      isLargeScreen,
    ],
  );

  if (!isInVirtualClass) return null;

  return (
    <PillBanner
      buttonChildren={<BannerButton />}
      className="max-w-[350px]"
      kind={bannerColor as PillBannerColorType}
      label={t("av.screenshare.banner.label")}
      textChildren={
        <IconText
          iconStart={<ScreenshareOutlineIcon className="text-white" />}
          text={bannerText}
          hideIconOnMd
          truncateText
        />
      }
    />
  );
};
